import { Component, EventEmitter, Input, Output, TemplateRef, ViewChild } from "@angular/core";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { backendMaxInt, PriceParamEnum } from "src/app/core/constants/constants";
import { Helper } from "src/app/core/helpers/helper";
import { VehicleService } from "../../services/vehicle.service";
import { CancellationPolicyModalComponent } from "../cancellation-policy-modal/cancellation-policy-modal.component";

@Component({
  selector: "app-view-fare-estimate-modal",
  templateUrl: "./view-fare-estimate-modal.component.html",
  styleUrl: "./view-fare-estimate-modal.component.scss",
  standalone: false
})
export class ViewFareEstimateModalComponent {
  modalRef!: NgbModalRef;
  modalConfig: any = { centered: true, backdrop: "static" };
  typeDetails: any;
  countrySettings: any;
  selectedVehicleDetails: any;

  distanceUnitString!: string;

  decimalPointValue: number = 0;
  setCurrencySign: number = 1;
  totalTax: number = 0;

  totalDistance!: string;
  totalPrice!: string;
  totalTime!: string;

  arrInvoiceElement: Array<any> = [];
  currencySign!: string;

  isInvoiceModal: boolean = false;

  @Input() isInvoice: boolean = false;
  @Input() accessibilityList: Array<any> = [];
  @Input() customPrices: Array<any> = [];
  isEditFareEstimate: boolean = false;
  isShowUpdatePrice: boolean = false;

  @ViewChild("template", { static: true }) template!: TemplateRef<any>;
  @ViewChild("cancellationPolicyModal", { static: true }) cancellationPolicyModal!: CancellationPolicyModalComponent;
  @Output() updateTotal = new EventEmitter();

  constructor(private modalService: NgbModal, public helper: Helper, private vehicleService: VehicleService) {}

  ngOnInit(): void {}

  /**
   * Displays the fare estimate modal with the provided details.
   * @param details The vehicle details.
   * @param countrySettings The country-specific settings.
   * @param distanceTime Optional distance and time information.
   */
  async showModal(details: any, countrySettings: any, distanceTime: any = null, isEditFareEstimate: boolean = false): Promise<void> {
    this.isShowUpdatePrice = false;
    this.modalRef = this.modalService.open(this.template, this.modalConfig);
    this.isEditFareEstimate = isEditFareEstimate;
    this.selectedVehicleDetails = JSON.parse(JSON.stringify(details));
    if (this.isInvoice) {
      this.typeDetails = JSON.parse(JSON.stringify(details.bookingInvoice.estimated));
    } else {
      this.typeDetails = JSON.parse(JSON.stringify(details.priceDetail));
    }

    this.countrySettings = countrySettings;
    this.currencySign = countrySettings.currencySign;
    this.decimalPointValue = countrySettings.decimalPointValue;
    if (distanceTime) {
      this.totalDistance = (distanceTime.totalDistance / 1000).toFixed(this.decimalPointValue);
      const minutes = Math.floor(distanceTime.totalTime / 60);
      const seconds = distanceTime.totalTime % 60;
      this.totalTime = `${minutes} ${this.helper.translateService.instant("label-title.min")} ${seconds} ${this.helper.translateService.instant("label-title.sec")}`;
    }
    this.distanceConversion();
    await this.getAccessibilityAndCustomPriceList();
    this.arrInvoiceElement = this.getInvoiceDetails();
    this.getPrice();
    this.totalTax = this.typeDetails.taxPrices?.reduce((total: number, tax: any) => total + tax.price, 0);

    this.modalRef.result.then(
      () => {
        this.closeModal();
      },
      () => {
        this.closeModal();
      }
    );
  }

  /**
   * Retrieves the accessibility and custom price list.
   * @returns A promise that resolves with the accessibility list.
   */
  getAccessibilityAndCustomPriceList(): Promise<any> {
    return new Promise((resolve) => {
      if (this.accessibilityList?.length > 0 || this.customPrices?.length > 0) {
        resolve(this.accessibilityList);
      } else {
        this.accessibilityList = [];
        this.customPrices = [];
        const parameters: any = {};
        this.vehicleService.getAccessibilityList(parameters).subscribe((response: any) => {
          if (response) {
            this.accessibilityList = response.accessibilities;
            this.customPrices = response.customPrices;
            resolve(this.accessibilityList);
          }
        });
      }
    });
  }

  /**
   * Closes the fare estimate modal.
   */
  closeModal(): void {
    this.modalRef?.dismiss();
  }

  updateFareEatime(): void {
    this.closeModal();
    const updatedChargesArray = this.arrInvoiceElement
      .filter((x: any) => x.multiplier !== undefined && x.multiplier !== null && x.multiplier !== 1)
      .map((x: any) => {
        return {
          title: x.chargeId && x.title !== "airportPickupPrice" ? x.chargeId : x.title,
          multiplier: x.multiplier,
          actualPrice: x.actualPrice,
          price: x.price,
          driverProfit: x.driverProfit
        };
      });
    this.updateTotal.emit(updatedChargesArray);
  }

  /**
   * Converts the distance unit based on country settings.
   */
  distanceConversion(): void {
    if (this.countrySettings.distanceUnit === this.helper.DistanceUnit.KM) {
      this.distanceUnitString = "label-title.km";
    } else {
      this.distanceUnitString = "label-title.mile";
    }
  }

  /**
   * Checks if the minimum fare is applied.
   * @returns True if the minimum fare is applied, otherwise false.
   */
  isMinfareApplied(): boolean {
    return this.typeDetails?.charges.some((item: any) => item.title === "minimumFee" && item.price > 0);
  }

  /**
   * Retrieves the invoice details.
   * @returns An array of invoice elements.
   */
  getInvoiceDetails(): Array<any> {
    let arrInvoiceElement: Array<any> = [];

    // Process charges
    this.typeDetails?.charges?.forEach((obj: any) => {
      const element = this.getStringFromPriceType(obj, this.helper.ChargeType.charges);
      if (element) arrInvoiceElement.push(element);
    });

    // Process additionalPrices
    this.typeDetails.additionalPrices?.forEach((obj: any) => {
      const element = this.getStringFromPriceType(obj, this.helper.ChargeType.additionalPrices);
      if (element) arrInvoiceElement.push(element);
    });

    if (this.isInvoiceModal) {
      // Process accessibilityPrices if not fare estimate
      this.typeDetails?.accessibilityPrices?.forEach((obj: any) => {
        const element = this.getStringFromPriceType(obj, this.helper.ChargeType.accessibilityPrices);
        if (element) arrInvoiceElement.push(element);
      });
    }

    arrInvoiceElement = this.setFormatedElements(arrInvoiceElement);

    return arrInvoiceElement;
  }

  /**
   * Generates a string representation of the price type.
   * @param price The price object.
   * @param chargeType The type of charge.
   * @returns A formatted price string.
   */
  getStringFromPriceType(price: any, chargeType: string): any {
    // if ((price.price ?? 0) <= 0) {
    //   return null;
    // }
    if (price.title === PriceParamEnum.platformProfit || [3, 4, 7].includes(price.type)) {
      return null;
    }

    let title: any = price.title ?? "";
    let chargeId: string = "";
    if (chargeType === this.helper.ChargeType.accessibilityPrices) {
      title = this.getTitleForAccessibility(price);
    } else if (chargeType === this.helper.ChargeType.additionalPrices) {
      chargeId = JSON.parse(JSON.stringify(price.chargeId));
      title = this.getTitleForCustomPrice(price);
    }
    const titleValue: string = this.helper.PriceParamEnumString[title] ?? title; // Assuming this is a method to localize strings
    const priceValue: string = this.formatPrice(price.price ?? 0); // Assuming this is a method to format prices

    if (this.isSimpleTitleCase(title)) {
      return this.getSimpleTitleCaseObject(titleValue, priceValue, price, title, chargeId);
    }

    if (this.isTimeDistanceCase(title)) {
      return this.getTimeDistancePriceElement(this.isDistancePrice(title), price, titleValue, priceValue, title, chargeId);
    }

    if (title === this.helper.PriceParamEnum.surgePrice) {
      return this.getSurgePriceObject(titleValue, priceValue, price, title, chargeId);
    }

    if (title) {
      return {
        strTitle: titleValue,
        title,
        chargeId,
        strAmount: priceValue,
        multiplier: price.multiplier,
        actualPrice: price.actualPrice ?? price.price,
        price: price.price,
        strSubTitle: ""
      };
    }

    return null;
  }

  /**
   * Retrieves the title for accessibility charges.
   * @param price The price object.
   * @returns The title for the accessibility charge.
   */
  getTitleForAccessibility(price: any): string {
    const title = this.accessibilityList?.find((pref) => pref._id === price.chargeId);

    let accessibility = "";
    if (title?.accessibility) {
      accessibility = title?.accessibility;
    } else if (price.title) {
      accessibility = price.title;
    }
    return accessibility;
  }

  /**
   * Retrieves the title for custom prices.
   * @param price The price object.
   * @returns The title for the custom price.
   */
  getTitleForCustomPrice(price: any): string {
    const customPrice = this.customPrices?.find((pref) => pref._id === price.chargeId);

    let customPriceTitle = price.title;
    if (customPrice?.title) {
      customPriceTitle = customPrice?.title;
    }
    return customPriceTitle;
  }

  /**
   * Checks if the title is a simple title case.
   * @param title The title to check.
   * @returns True if the title is a simple title case, otherwise false.
   */
  isSimpleTitleCase(title: any): boolean {
    const simpleTitleCases = [
      this.helper.PriceParamEnum.bookingFee,
      this.helper.PriceParamEnum.minimumFee,
      this.helper.PriceParamEnum.cancellationPrice,
      this.helper.PriceParamEnum.promoBonus
    ];
    return simpleTitleCases.includes(title);
  }

  /**
   * Generates an object for simple title cases.
   * @param titleValue The title value.
   * @param priceValue The price value.
   * @returns An object representing the simple title case.
   */
  getSimpleTitleCaseObject(titleValue: string, priceValue: string, price: any, title: any, chargeId: string): any {
    return {
      strTitle: titleValue,
      title,
      chargeId,
      strAmount: priceValue,
      multiplier: price.multiplier,
      actualPrice: price.actualPrice ?? price.price,
      price: price.price,
      strSubTitle: ""
    };
  }

  /**
   * Checks if the title is a time or distance case.
   * @param title The title to check.
   * @returns True if the title is a time or distance case, otherwise false.
   */
  isTimeDistanceCase(title: any): boolean {
    const timeDistanceCases = [
      this.helper.PriceParamEnum.distancePrice,
      this.helper.PriceParamEnum.timePrice,
      this.helper.PriceParamEnum.waitingTimePrice,
      this.helper.PriceParamEnum.airportWaitingTimePrice,
      this.helper.PriceParamEnum.stopWaitingTimePrice,
      this.helper.PriceParamEnum.trafficTimePrice
    ];
    return timeDistanceCases.includes(title);
  }

  /**
   * Checks if the title is a distance price.
   * @param title The title to check.
   * @returns True if the title is a distance price, otherwise false.
   */
  isDistancePrice(title: any): boolean {
    return title === this.helper.PriceParamEnum.distancePrice;
  }

  /**
   * Generates an object for surge price cases.
   * @param titleValue The title value.
   * @param priceValue The price value.
   * @param price The price object.
   * @returns An object representing the surge price case.
   */
  getSurgePriceObject(titleValue: string, priceValue: string, price: any, title: any, chargeId: string): any {
    const subString = this.getSurgePriceSubString(price);
    return {
      strTitle: titleValue,
      title,
      strAmount: priceValue,
      multiplier: price.multiplier,
      price: price.price,
      actualPrice: price.actualPrice ?? price.price,
      chargeId,
      strSubTitle: subString
    };
  }

  /**
   * Generates an object for time and distance price elements.
   * @param isDistance Indicates if the price is for distance.
   * @param price The price object.
   * @param titleValue The title value.
   * @param priceValue The price value.
   * @returns An object representing the time and distance price element.
   */
  getTimeDistancePriceElement(isDistance: boolean, price: any, titleValue: string, priceValue: string, title: any, chargeId: string): any {
    const strUnit: string = isDistance ? this.helper.translateService.instant(this.distanceUnitString) : this.helper.translateService.instant("label-title.min");

    if (price.isApplySlotPrice) {
      let strSubTitle: string = "";

      price.appliedSlots?.forEach((obj: any, i: number) => {
        const min: number = obj.min ?? 0;
        const max: number = obj.max ?? 0;
        const isLast: any = i === (price.appliedSlots?.length ?? 0) - 1;
        const unitValue: any = this.formatPrice(obj.unitPrice ?? 0);
        const slotRange: any = this.getSlotRange(min, max, strUnit);
        const slotPriceValue: any = this.formatPrice(obj.price ?? 0);
        const strSub: string = this.getStrSub(price, unitValue, strUnit);
        strSubTitle = this.getStrSubTitle(price, slotRange, strSub, slotPriceValue, min, max, isLast, strSubTitle);
      });

      return {
        strTitle: titleValue,
        title,
        strAmount: priceValue,
        price: price.price,
        multiplier: price.multiplier,
        actualPrice: price.actualPrice ?? price.price,
        chargeId,
        strSubTitle: strSubTitle
      };
    }

    const unitValue = this.formatPrice(price.unitPrice ?? 0);
    const strSubTitle = `${unitValue}/${strUnit}`;

    return {
      strTitle: titleValue,
      title,
      strAmount: priceValue,
      price: price.price,
      actualPrice: price.actualPrice ?? price.price,
      chargeId,
      multiplier: price.multiplier,
      strSubTitle: strSubTitle
    };
  }

  /**
   * Generates a slot range string.
   * @param min The minimum value.
   * @param max The maximum value.
   * @param strUnit The unit string.
   * @returns The slot range string.
   */
  getSlotRange(min: any, max: number, strUnit: string): string {
    const maxString = max === backendMaxInt ? "∞" : max.toString();
    return `${min} ${strUnit} - ${maxString} ${strUnit}`;
  }

  /**
   * Generates a surge price substring.
   * @param price The price object.
   * @returns The surge price substring.
   */
  getSurgePriceSubString(price: any): string {
    let subString: string = "";

    if (price?.unit > 0) {
      subString = `${price.unit} X`;
    }

    return subString;
  }

  /**
   * Generates a substring for the price.
   * @param price The price object.
   * @param unitValue The unit value.
   * @param strUnit The unit string.
   * @returns The price substring.
   */
  getStrSub(price: any, unitValue: string, strUnit: string): string {
    let strSub = `${unitValue}/${strUnit}`;

    if (!price.isSlotInPriceWithUnitCalculation) {
      strSub = unitValue;
    }

    return strSub;
  }

  /**
   * Generates a subtitle string for the price.
   * @param price The price object.
   * @param slotRange The slot range string.
   * @param strSub The price substring.
   * @param priceValue The price value.
   * @param min The minimum value.
   * @param max The maximum value.
   * @param isLast Indicates if it is the last element.
   * @param strSubTitle The subtitle string.
   * @returns The generated subtitle string.
   */
  // eslint-disable-next-line max-params
  getStrSubTitle(price: any, slotRange: any, strSub: string, priceValue: any, min: number, max: number, isLast: any, strSubTitle: string): string {
    if (price.isSlotInPriceWithSum) {
      strSubTitle += `${slotRange} ( ${strSub} ):${priceValue}${isLast ? "" : "\n"}`;
    } else if ((price.unit ?? 0) >= min && (price.unit ?? 0) <= max && !isLast) {
      strSubTitle += `${slotRange} ( ${strSub} ):${priceValue}${isLast ? "" : "\n"}`;
      return strSubTitle;
    } else if (isLast) {
      strSubTitle += `${slotRange} ( ${strSub} ):${priceValue}${isLast ? "" : "\n"}`;
    }

    return strSubTitle;
  }

  /**
   * Formats the price to a string.
   * @param amount The amount to format.
   * @returns The formatted price string.
   */
  formatPrice(amount: number): string {
    let priceString: string = "";
    const price: string = amount.toFixed(this.decimalPointValue);

    if (this.setCurrencySign === this.helper.SetCurrencySignUnit.LEFT) {
      priceString = `${this.currencySign} ${price}`;
    } else {
      priceString = `${price} ${this.currencySign}`;
    }

    return priceString;
  }

  /**
   * Sets formatted elements for the invoice.
   * @param arrInvoiceElement The array of invoice elements.
   * @returns The array of formatted invoice elements.
   */
  setFormatedElements(arrInvoiceElement: Array<any>): Array<any> {
    return arrInvoiceElement.map((item) => {
      if (!item.strSubTitle) {
        return { ...item, strSubTitleArray: [] };
      }

      const subtitleArray = item.strSubTitle.split("\n");
      const strSubTitleArray = subtitleArray.map((subtitle: any) => {
        const [title, value] = subtitle.split(":");
        return { title, value };
      });

      return { ...item, strSubTitleArray };
    });
  }

  /**
   * Retrieves the total price, distance, and time.
   * @returns The total price, distance, and time.
   */
  getPrice(): any | null {
    for (const detail of this.typeDetails.charges) {
      switch (detail.title) {
        case this.helper.PriceParamEnum.bookingFee: {
          this.totalPrice = this.typeDetails.total.toFixed(this.decimalPointValue);
          break;
        }
        case this.helper.PriceParamEnum.distancePrice: {
          this.totalDistance = `${detail.unit.toFixed(this.decimalPointValue)}`;
          break;
        }
        case this.helper.PriceParamEnum.timePrice: {
          const time = detail.unit.toFixed(this.decimalPointValue);
          const minutes = Math.floor(time);
          const seconds = Math.round((time - minutes) * 60);
          this.totalTime = `${minutes} ${this.helper.translateService.instant("label-title.min")} ${seconds} ${this.helper.translateService.instant("label-title.sec")}`;
          break;
        }
        default:
          break;
      }
    }
    return null;
  }

  updatePrice(element: any): void {
    let multiplier = element.price / element.actualPrice;
    if (element.actualPrice === 0) {
      multiplier = -1;
    }

    let chargeElementIndex;
    let arrInvoiceElementIndex;
    let chargeElement;
    if (element.chargeId) {
      arrInvoiceElementIndex = this.arrInvoiceElement.findIndex((x: any) => x.chargeId === element.chargeId);
      chargeElementIndex = this.typeDetails.additionalPrices.findIndex((x: any) => x.chargeId === element.chargeId);
      chargeElement = JSON.parse(JSON.stringify(this.typeDetails.additionalPrices[chargeElementIndex]));
    } else {
      arrInvoiceElementIndex = this.arrInvoiceElement.findIndex((x: any) => x.title === element.title);
      chargeElementIndex = this.typeDetails.charges.findIndex((x: any) => x.title === element.title);
      chargeElement = JSON.parse(JSON.stringify(this.typeDetails.charges[chargeElementIndex]));
    }

    const chargeMultiplier = element.price / chargeElement.price;

    chargeElement.price = element.price;
    chargeElement.discountedPrice = chargeElement.discountedPrice * chargeMultiplier;
    chargeElement.driverProfit = chargeElement.driverProfit * chargeMultiplier;

    if (chargeElement.appliedSlots) {
      chargeElement.appliedSlots.forEach((appliedSlot: any) => {
        appliedSlot.price = appliedSlot.price * chargeMultiplier;
        appliedSlot.unitPrice = appliedSlot.unitPrice * chargeMultiplier;
        appliedSlot.driverProfit = appliedSlot.driverProfit * chargeMultiplier;
      });
    }
    const newElement = this.getStringFromPriceType(chargeElement, element.chargeId ? this.helper.ChargeType.additionalPrices : this.helper.ChargeType.charges);

    newElement.actualPrice = element.actualPrice;
    newElement.multiplier = multiplier;
    if (element.chargeId) {
      this.typeDetails.additionalPrices[chargeElementIndex] = JSON.parse(JSON.stringify(newElement));
    } else {
      this.typeDetails.charges[chargeElementIndex] = JSON.parse(JSON.stringify(newElement));
    }
    this.arrInvoiceElement[arrInvoiceElementIndex] = JSON.parse(JSON.stringify(newElement));
    this.arrInvoiceElement = this.setFormatedElements(this.arrInvoiceElement);
    this.isShowUpdatePrice = true;
  }

  /**
   * Opens the cancellation policy modal.
   */
  onCancellationPolicyModal(): void {
    this.cancellationPolicyModal.openModal(this.selectedVehicleDetails._id);
  }
}
